import { FormattedMessage, useIntl } from 'react-intl'
import { isEmpty } from 'lodash'

import { CopyToClipboard, CopyToClipboardWrapper } from '@10xLabs/react-web/components/CopyToClipboard'
import { FormattedDate } from '@10xLabs/react-web/components/FormattedDate'
import { FormattedTimeByDate } from '@10xLabs/react-web/components/FormattedTimeByDate'
import { TableContainer, Table, Thead, Th, Tbody, Tr, Td } from 'components/v2/Table'
import { CouponDetails } from 'components/CouponDetails'
import { TotalWithDiscount } from 'components/TotalWithDiscount'
import { SecondaryInfo } from 'features/App/components/SecondaryInfo'
import { paymentMethodMessages } from 'features/Bookings/components/PaymentMethod/messages'
import { FEATURES } from 'features/Roles/components/Permissions/constants'
import { SaleStatus } from 'features/Sales/components/Status'
import { getLineItemsDiscount, getLineItemsTotal } from 'resolvers/sales'
import { safeFormatMessage } from 'utils/intl'
import { canCancel } from 'features/Users/utils'
import { SaleActions } from './Actions'
import { LineItems } from './LineItems'
import { ReactElement } from 'react'
import { GTSale } from 'gql/types.generated'
import { useLogin } from 'features/Login/hooks'

export const SalesList = ({ sales = [] }: { sales: GTSale[] }): ReactElement => {
  const intl = useIntl()
  const login = useLogin()

  return (
    <TableContainer overflow>
      <Table marginBottom>
        <Thead>
          <Tr>
            {canCancel(FEATURES.SALE, login.accessToken.policies) && <Th />}
            <Th>
              <FormattedMessage id="dictionary.id" defaultMessage="Id" />
            </Th>
            <Th>
              <FormattedMessage id="spanish.sales.list.status.label" defaultMessage="Estado" />
            </Th>
            <Th>
              <FormattedMessage id="spanish.sales.list.createdAt.label" defaultMessage="Fecha de compra" />
            </Th>
            <Th>
              <FormattedMessage id="spanish.sales.list.products.label" defaultMessage="Productos" />
            </Th>
            <Th>
              <FormattedMessage id="spanish.sales.list.total.label" defaultMessage="Total" />
            </Th>
            <Th>
              <FormattedMessage id="spanish.sales.list.paymentMethod.label" defaultMessage="Método de pago" />
            </Th>
            <Th>
              <FormattedMessage id="spanish.sales.list.routesAndSegments.label" defaultMessage="Ruta y segmento" />
            </Th>
            <Th>
              <FormattedMessage id="spanish.sales.list.buyer.label" defaultMessage="Cliente" />
            </Th>
            <Th>
              <FormattedMessage id="spanish.sales.list.cart.label" defaultMessage="Carrito" />
            </Th>
            <Th>
              <FormattedMessage id="spanish.sales.list.seller.label" defaultMessage="Vendedor" />
            </Th>
          </Tr>
        </Thead>

        <Tbody>
          {sales.map((sale): ReactElement => {
            const total = getLineItemsTotal(sale.lineItems)
            const discount = getLineItemsDiscount(sale.lineItems)

            return (
              <Tr key={sale.id}>
                {canCancel(FEATURES.SALE, login.accessToken.policies) && (
                  <Td top>
                    <SaleActions sale={sale} />
                  </Td>
                )}
                <Td top>
                  <CopyToClipboardWrapper>
                    <div>{sale.id.substring(0, 8)}</div>
                    <CopyToClipboard str={sale.id} />
                  </CopyToClipboardWrapper>
                </Td>
                <Td top>
                  <SaleStatus status={sale.status} />
                </Td>
                <Td top>
                  <FormattedDate intl={intl} value={sale.createdAt} month="2-digit" day="2-digit" year="numeric" />
                  <SecondaryInfo>
                    <FormattedTimeByDate intl={intl} date={sale.createdAt} />
                  </SecondaryInfo>
                </Td>
                <Td top>
                  <LineItems lineItems={sale.lineItems} />
                </Td>
                <Td top>
                  <TotalWithDiscount total={total} discount={discount} />
                  <CouponDetails coupon={sale.coupon} />
                </Td>
                <Td top>
                  {safeFormatMessage({ intl, messages: paymentMethodMessages, key: sale.payment.method })}
                  {sale.payment.last4 && <>&nbsp;{sale.payment.last4}</>}
                </Td>
                <Td top>
                  {!isEmpty(sale.trip) ? (
                    <>
                      <div>
                        R: {sale.trip.origin.name} - {sale.trip.destination.name}
                      </div>
                      <div>
                        S: {sale.trip.segment.origin.name} - {sale.trip.segment.destination.name}
                      </div>
                    </>
                  ) : (
                    'N/A'
                  )}
                </Td>
                <Td top>
                  {sale.buyer && sale.buyer.firstName ? (
                    <>
                      <div>
                        {sale.buyer.firstName} {sale.buyer.lastName}
                      </div>
                      {sale.buyer.email && <div>{sale.buyer.email}</div>}
                      {sale.buyer.seat && sale.buyer.seat.label && <div>{sale.buyer.seat.label}</div>}
                    </>
                  ) : (
                    'N/A'
                  )}
                </Td>
                <Td top>{sale.cart.name}</Td>
                <Td top>
                  {sale.seller.username} ({sale.seller.firstName} {sale.seller.lastName})
                </Td>
              </Tr>
            )
          })}
        </Tbody>
      </Table>
    </TableContainer>
  )
}
